import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { Sticker } from './Cartoons';

interface ActivityItem {
  type: string;
  title: string;
  description: string;
  icon: string;
  timestamp: string;
  link: string;
}

/** Soft gradient per activity type for the sticker disc. */
const TINT: Record<string, [string, string]> = {
  SUMMARY: ['#FDE68A', '#FCA5A5'],
  HOMEWORK: ['#BFDBFE', '#A5B4FC'],
  MATH: ['#FED7AA', '#FDBA74'],
  READING: ['#DDD6FE', '#C4B5FD'],
  PHOTO: ['#A7F3D0', '#6EE7B7'],
  VIDEO: ['#BAE6FD', '#7DD3FC'],
  FRIEND: ['#FBCFE8', '#F9A8D4'],
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface ActivityFeedProps {
  /** Explicit endpoint to fetch the feed from. Overrides kidId when provided. */
  endpoint?: string;
  /** Kid id — builds /api/kids/{kidId}/activities when endpoint is omitted. */
  kidId?: number | string | null;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ endpoint, kidId }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  const url = endpoint ?? (kidId != null ? `/api/kids/${kidId}/activities` : null);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    api.get(url)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [url]);

  if (loading) return <p className="text-center text-gray-400 py-8">Loading activities…</p>;

  if (items.length === 0) {
    return (
      <div className="kt-card p-8 text-center">
        <Sticker emoji="🌟" from="#FDE68A" to="#FCA5A5" size={64} className="mx-auto mb-3" />
        <p className="font-display font-bold text-gray-700">No activities yet</p>
        <p className="text-sm text-gray-500 mt-1">Take a quiz or check your homework to get started!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((it, i) => {
        const [from, to] = TINT[it.type] || ['#E5E7EB', '#D1D5DB'];
        return (
          <button
            key={i}
            onClick={() => navigate(it.link)}
            className="kt-card w-full text-left p-3 flex items-center gap-3 hover:shadow-md transition active:scale-[0.99]"
          >
            <Sticker emoji={it.icon} from={from} to={to} size={48} />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-800 truncate">{it.title}</p>
              {it.description && (
                <p className="text-sm text-gray-500 truncate">{it.description}</p>
              )}
            </div>
            <span className="text-[11px] text-gray-400 font-semibold whitespace-nowrap">
              {relativeTime(it.timestamp)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default ActivityFeed;
